import { Box, Flex } from '@chakra-ui/react';
import React from 'react';
import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';

import { BusCaConfigEditor } from './BusCaConfigEditor';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { DealTapeBuilderView } from '~/backup/shared/views/deal-tape-builder/DealTapeBuilderView';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { BusDealProfileArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/BusDealProfileArtifact';
import { BusDeliverableConfigArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/BusDeliverableConfigArtifact';
import { BusReportConfigArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/BusReportConfigArtifact';

type SettingsSection = {
  slug: string;
  label: string;
  /** Only admins see the link / can reach the route. */
  adminOnly: boolean;
};

const SETTINGS_SECTIONS: SettingsSection[] = [
  { slug: 'deal-profile', label: 'Deal Profile', adminOnly: false },
  { slug: 'report-config', label: 'Report Config', adminOnly: false },
  { slug: 'deliverable-config', label: 'Deliverable Config', adminOnly: false },
  { slug: 'pipeline-config', label: 'Pipeline Config', adminOnly: true },
];

const SubNavLink = ({
  to,
  label,
}: {
  to: string;
  label: string;
}): React.ReactElement => (
  <NavLink to={to} end>
    {({ isActive }) => (
      <Box
        px="0.75rem"
        py="0.5rem"
        fontSize="0.875rem"
        fontWeight={isActive ? 600 : 500}
        color={isActive ? colors.gray[900] : colors.gray[500]}
        borderBottom="2px solid"
        borderColor={isActive ? colors.gray[900] : 'transparent'}
      >
        {label}
      </Box>
    )}
  </NavLink>
);

/**
 * BUS deal Settings hub (lender portal). A single "Settings" deal-nav entry opens
 * this hub, which groups the deal's configuration into sub-sections behind a
 * secondary nav: **Deal Profile**, **Report Config** + **Deliverable Config** (the
 * simplified CA editor, editable by admins / read-only for members) and **Pipeline Config**
 * (the tape-builder graph, admin-only). Sections are real nested routes under
 * `/deals/:implementationId/settings`, so each is independently deep-linkable.
 */
export const DealSettingsHub = (): React.ReactElement => {
  const { user } = useUserAuthOrThrow();
  const { implementationId } = useParams();
  const isAdmin = user.roleType === 'ADMIN';

  if (!implementationId) return <Navigate to="/portfolio" replace />;

  const base = `/deals/${implementationId}/settings`;
  const visibleSections = SETTINGS_SECTIONS.filter(
    (section) => !section.adminOnly || isAdmin,
  );

  return (
    <Flex direction="column" p={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}>
      <Typography variant="section-title">Settings</Typography>
      <Flex
        mt="1rem"
        mb="1.5rem"
        gap="0.5rem"
        borderBottom="1px solid"
        borderColor={colors.gray[200]}
      >
        {visibleSections.map((section) => (
          <SubNavLink
            key={section.slug}
            to={`${base}/${section.slug}`}
            label={section.label}
          />
        ))}
      </Flex>

      <Routes>
        <Route index element={<Navigate to="deal-profile" replace />} />
        <Route
          path="deal-profile"
          element={
            <BusCaConfigEditor
              artifactComponent={BusDealProfileArtifact}
              emptyLabel="Deal Profile"
            />
          }
        />
        <Route
          path="report-config"
          element={
            <BusCaConfigEditor
              artifactComponent={BusReportConfigArtifact}
              emptyLabel="Report Config"
            />
          }
        />
        <Route
          path="deliverable-config"
          element={
            <BusCaConfigEditor
              artifactComponent={BusDeliverableConfigArtifact}
              emptyLabel="Deliverable Config"
            />
          }
        />
        {isAdmin && (
          <Route path="pipeline-config" element={<DealTapeBuilderView />} />
        )}
        <Route path="*" element={<Navigate to="deal-profile" replace />} />
      </Routes>
    </Flex>
  );
};
